/**
 * FeedbackButton — TopBar entry point for the Send Feedback flow.
 *
 * Hidden behind `REACT_APP_FEEDBACK_ENABLED` so the megaphone does
 * not appear in environments where the backend endpoint is not yet
 * deployed. Once the BE rolls out everywhere, flip the env var to
 * `true` in production / testing too and drop this gate in a
 * follow-up PR.
 *
 * Matches the IconPill styling used by NotificationsBell so the
 * three trailing icons (feedback / bell / user) read as one strip.
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@shared/ui';
import FeedbackDialog from './FeedbackDialog';

const IconPill = styled('button')({
  position: 'relative',
  width: 38,
  height: 38,
  borderRadius: 8,
  background: 'transparent',
  border: '1px solid transparent',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--qhs-text-2)',
  cursor: 'pointer',
  transition: 'all 150ms',
  padding: 0,
  '&:hover': {
    background: 'var(--qhs-surface-hover)',
    color: 'var(--qhs-brand)',
  },
});

const MegaphoneIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const tooltip = useSelector(state => state.i18n?.feedback?.triggerTooltip) || 'Send feedback';

  if (process.env.REACT_APP_FEEDBACK_ENABLED !== 'true') return null;

  return (
    <>
      <IconPill type="button" title={tooltip} onClick={() => setOpen(true)}>
        <MegaphoneIcon />
      </IconPill>
      <FeedbackDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default FeedbackButton;
